/**
 * ACRES Admin — Lucky Draw Manager
 * - List monthly draws, configure prizes, run draw, view winners.
 */
import React, { useEffect, useState } from 'react';
import { acresAdmin } from '../../../lib/services/acresService';

interface Draw { id: string; month_year: string; status: string; total_entries?: number; created_at: string; drawn_at?: string; }
interface Prize { id?: string; draw_id?: string; rank: number; title: string; description?: string; prize_type: string; prize_value?: number; quantity: number; }

const PRIZE_TYPES = ['shares','cash','gold','merchandise','vip_access','recognition','event','travel','founder_reward','other'];

export const AcresLuckyDraws: React.FC<{ currentUser?: any }> = () => {
  const [draws, setDraws] = useState<Draw[]>([]);
  const [active, setActive] = useState<Draw | null>(null);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [winners, setWinners] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newMonth, setNewMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const loadDraws = async () => {
    try { const r = await acresAdmin.listDraws(); setDraws(r.draws || []); }
    catch (e: any) { setError(e?.message || 'Failed to load draws'); }
  };
  useEffect(() => { loadDraws(); }, []);

  const openDraw = async (d: Draw) => {
    setActive(d); setError(null);
    try {
      const r = await acresAdmin.getDraw({ id: d.id });
      setPrizes(r.prizes || []);
      const w = await acresAdmin.listWinners(d.id);
      setWinners(w.winners || []);
    } catch (e: any) { setError(e?.message || 'Failed to load draw'); }
  };

  const createDraw = async () => {
    if (!/^\d{4}-\d{2}$/.test(newMonth)) { alert('Use YYYY-MM'); return; }
    try { await acresAdmin.upsertDraw({ month_year: newMonth + '-01', status: 'scheduled' }); await loadDraws(); }
    catch (e: any) { alert(e?.message || 'Create failed'); }
  };

  const setStatus = async (status: string) => {
    if (!active) return;
    try { await acresAdmin.setDrawStatus(active.id, status); await loadDraws(); await openDraw(active); }
    catch (e: any) { alert(e?.message || 'Status update failed'); }
  };

  const addPrize = () => setPrizes(p => [...p, { rank: p.length + 1, title: '', prize_type: 'shares', quantity: 1, draw_id: active?.id }]);
  const updatePrize = (i: number, patch: Partial<Prize>) => setPrizes(p => p.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const savePrize = async (i: number) => {
    const p = prizes[i]; if (!active) return;
    try { const r = await acresAdmin.upsertPrize({ ...p, draw_id: active.id }); setPrizes(arr => arr.map((x, idx) => idx === i ? { ...x, id: r.prize?.id || x.id } : x)); }
    catch (e: any) { alert(e?.message || 'Save prize failed'); }
  };
  const removePrize = async (i: number) => {
    const p = prizes[i];
    if (p.id) { if (!confirm('Delete this prize?')) return; try { await acresAdmin.deletePrize(p.id); } catch (e: any) { alert(e?.message); return; } }
    setPrizes(arr => arr.filter((_, idx) => idx !== i));
  };

  const runDraw = async () => {
    if (!active) return;
    if (!confirm(`Run lucky draw for ${active.month_year}? This will lock entries and pick winners deterministically. This action is final.`)) return;
    const seed = prompt('Optional seed (leave empty for random):') || undefined;
    try { await acresAdmin.runDraw(active.id, seed); await openDraw(active); await loadDraws(); }
    catch (e: any) { alert(e?.message || 'Run draw failed'); }
  };

  return (
    <div>
      {error && <div style={errBox}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input type="month" value={newMonth} onChange={e => setNewMonth(e.target.value)} style={{ ...input, width: 160 }} />
        <button onClick={createDraw} style={btnPrimary}>+ Create Draw</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: active ? '1fr 1.4fr' : '1fr', gap: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr style={trHead}><th style={th}>Month</th><th style={th}>Status</th><th style={th}>Entries</th><th style={th}></th></tr></thead>
            <tbody>
              {draws.map(d => (
                <tr key={d.id} style={trBody}>
                  <td style={td}>{d.month_year?.slice(0, 7)}</td>
                  <td style={td}>{d.status}</td>
                  <td style={td}>{d.total_entries ?? '—'}</td>
                  <td style={td}><button onClick={() => openDraw(d)} style={btnSmall}>Open</button></td>
                </tr>
              ))}
              {!draws.length && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>No draws yet</td></tr>}
            </tbody>
          </table>
        </div>

        {active && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 700 }}>Draw {active.month_year?.slice(0,7)} · {active.status}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {active.status === 'scheduled' && <button onClick={() => setStatus('open')} style={btnSmall}>Open</button>}
                {active.status === 'open' && <button onClick={runDraw} style={{ ...btnSmall, borderColor: '#F59E0B', color: '#F59E0B' }}>🎰 Run Draw</button>}
                {active.status === 'open' && <button onClick={() => setStatus('cancelled')} style={btnSmall}>Cancel</button>}
                <button onClick={() => setActive(null)} style={btnSmall}>Close</button>
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Prizes</div>
            {prizes.map((p, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 130px 90px 80px auto', gap: 6, marginBottom: 6 }}>
                <input type="number" value={p.rank} onChange={e => updatePrize(i, { rank: Number(e.target.value) })} style={input} />
                <input placeholder="Title" value={p.title} onChange={e => updatePrize(i, { title: e.target.value })} style={input} />
                <select value={p.prize_type} onChange={e => updatePrize(i, { prize_type: e.target.value })} style={input}>{PRIZE_TYPES.map(t => <option key={t}>{t}</option>)}</select>
                <input type="number" placeholder="Value" value={p.prize_value || ''} onChange={e => updatePrize(i, { prize_value: Number(e.target.value) || undefined })} style={input} />
                <input type="number" value={p.quantity} onChange={e => updatePrize(i, { quantity: Number(e.target.value) })} style={input} />
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => savePrize(i)} style={btnSmall}>Save</button>
                  <button onClick={() => removePrize(i)} style={{ ...btnSmall, color: '#F87171' }}>×</button>
                </div>
              </div>
            ))}
            <button onClick={addPrize} style={{ ...btnSmall, marginTop: 4 }}>+ Add Prize</button>

            <div style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 6px' }}>Winners ({winners.length})</div>
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {winners.map((w, i) => (
                <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #1E1E1E', fontSize: 12 }}>
                  #{w.rank} · {w.user_handle || `user#${w.user_id}`} · {w.prize_title} ({w.prize_type})
                </div>
              ))}
              {!winners.length && <div style={{ color: '#9CA3AF', fontSize: 12 }}>No winners drawn yet</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { background: '#1E1E1E' };
const trBody: React.CSSProperties = { borderBottom: '1px solid #1E1E1E' };
const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#F59E0B', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px' };
const card: React.CSSProperties = { background: '#0F0F0F', border: '1px solid #1E1E1E', borderRadius: 8, padding: 14 };
const input: React.CSSProperties = { padding: '6px 8px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 4, color: '#FFF', fontSize: 12 };
const btnPrimary: React.CSSProperties = { padding: '8px 14px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' };
const btnSmall: React.CSSProperties = { padding: '4px 10px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 4, fontSize: 12, cursor: 'pointer' };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
